import { useUserRoles } from '@anytag/auth';
import { UNEXPECTED_ERROR } from '@anytag/shared-constants';
import { useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import { Button, ButtonProps, BaseModal } from '@anytag/shared-atoms';
import {
  TikTokSpecialCampaignDetail,
  TikTokSpecialCampaignStatus,
  useApproveTiktokSpecialCampaignMutation
} from '@anytag/graphql';

interface ApproveButtonProps extends ButtonProps {
  tiktokSpecialCampaign?: TikTokSpecialCampaignDetail;
}

export const ApproveButton = ({ tiktokSpecialCampaign, ...buttonProps }: ApproveButtonProps) => {
  const confirmModal = useToggleState();
  const { isAdminStaff } = useUserRoles();
  const { t, enqueueSnackbar, invalidateRouteLoader } = useQueryHelper();
  const { callApproveTiktokSpecialCampaign, loading } = useApproveTiktokSpecialCampaignMutation({
    onCompleted: invalidateRouteLoader
  });

  // TODO: we might need to check permissions here as well
  // Asking: https://anymindgroup.slack.com/archives/C02C5J5B7KL/p1730428908925939
  const hasApproveButton =
    isAdminStaff && tiktokSpecialCampaign && tiktokSpecialCampaign?.status === TikTokSpecialCampaignStatus.REVIEWING;

  const handleApprove = async () => {
    if (!hasApproveButton) return;

    try {
      await callApproveTiktokSpecialCampaign({ variables: { input: { campaignId: tiktokSpecialCampaign.id } } });

      enqueueSnackbar(t('succeededInUpdating', { name: tiktokSpecialCampaign.title }), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error?.message || UNEXPECTED_ERROR), { variant: 'error' });
    } finally {
      confirmModal.close();
    }
  };

  return (
    hasApproveButton && (
      <>
        <Button
          variant="solid"
          onClick={confirmModal.open}
          title={t('Button.Approve')}
          css={{ padding: '0 24px', minWidth: '100px' }}
          {...buttonProps}
        />

        <BaseModal
          open={confirmModal.status}
          onClose={confirmModal.close}
          title={t('Modal.approveTikTokCampaignTitle')}
          description={t('Modal.approveTikTokCampaignDescription')}
          footer={{
            cancelButton: { onClick: confirmModal.close },
            submitButton: {
              onClick: handleApprove,
              loading: { showIcon: true, status: loading },
              title: t(loading ? 'Button.Approving' : 'Button.Approve')
            }
          }}
        />
      </>
    )
  );
};
